import { useState, useEffect } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, ListNumbers, ArrowUp, ArrowDown, Lightning, Clock, Note } from "@phosphor-icons/react";

export default function DynamicSequence() {
  const { data, loading, reload } = useFetch(() => api.get("/founder/dynamic-sequence").then((r) => r.data), []);
  const [edit, setEdit] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [ticking, setTicking] = useState(false);
  const items = data?.items || [];

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta dinámica de la secuencia? Las posiciones siguientes se recompactan.")) return;
    try { await api.delete(`/founder/dynamic-sequence/${id}`); toast.success("Dinámica eliminada"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (item) => {
    try { await api.patch(`/founder/dynamic-sequence/${item.id}`, { active: !item.active }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const ordered = [...items];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    try {
      await api.post("/founder/dynamic-sequence/reorder", { ordered_ids: ordered.map((i) => i.id) });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const runTickNow = async () => {
    setTicking(true);
    try {
      const { data: r } = await api.post("/founder/dynamic-sequence/tick");
      toast.success(`Tick ejecutado · enviadas ${r.sent || 0} · nuevos enrolados ${r.new_enrollments || 0}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al ejecutar tick"); }
    finally { setTicking(false); }
  };

  return (
    <>
      <PageHeader
        label="Founder Pipeline"
        title="Secuencia de Dinámicas"
        description="Dinámicas ordenadas que cada fundador activo recibe de forma secuencial (Dinámica 1 día 1, Dinámica 2 día 2…), a la hora configurada. Las respuestas quedan en la bitácora de cada lead."
      >
        <button onClick={runTickNow} disabled={ticking} data-testid="run-tick-btn"
          className="text-sm border border-forest text-forest px-3 py-2 rounded flex items-center gap-2 hover:bg-forest/5 disabled:opacity-60">
          <Lightning size={14} /> {ticking ? "Ejecutando..." : "Ejecutar ahora"}
        </button>
        <button onClick={() => setShowCreate(true)} data-testid="add-dynamic-btn"
          className="bg-terracotta text-white text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-terracotta-hover">
          <Plus size={14} /> Agregar dinámica
        </button>
      </PageHeader>
      <PageBody>
        <Card className="p-4 mb-4 bg-page" testid="dynseq-info">
          <div className="flex items-start gap-3 text-xs text-carbon-700 leading-relaxed">
            <ListNumbers size={18} className="text-terracotta shrink-0 mt-0.5" />
            <div>
              Cada fundador en stage <span className="font-mono">founder_confirmed / active / core / leader_potential</span> se
              enrola automáticamente. Un job recurrente envía la siguiente dinámica de la secuencia una vez por día,
              respetando la hora de envío de cada dinámica (zona horaria configurable). La respuesta del lead se guarda
              en la <strong>Bitácora de Dinámicas</strong> dentro de su perfil.
            </div>
          </div>
        </Card>

        {loading ? <div className="text-carbon-500">Cargando...</div> : (
          <div className="space-y-3">
            {items.map((it, idx) => (
              <Card key={it.id} testid={`dynseq-card-${it.position}`} className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <div className="w-9 h-9 rounded-full bg-terracotta-light text-terracotta font-heading font-bold flex items-center justify-center" data-testid={`dynseq-pos-${it.position}`}>
                      {it.position}
                    </div>
                    <div className="flex flex-col">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`dynseq-up-${it.position}`}
                        className="text-carbon-400 hover:text-terracotta disabled:opacity-30"><ArrowUp size={14} /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`dynseq-down-${it.position}`}
                        className="text-carbon-400 hover:text-terracotta disabled:opacity-30"><ArrowDown size={14} /></button>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge tone={it.active ? "forest" : "neutral"}>{it.active ? "activa" : "pausada"}</Badge>
                      <span className="inline-flex items-center gap-1 text-xs text-carbon-500">
                        <Clock size={12} /> {it.send_hour} · {it.timezone}
                      </span>
                      {it.template_id ? (
                        <span className="inline-flex items-center gap-1 text-xs text-terracotta">
                          <Note size={12} /> usa template aprobada
                        </span>
                      ) : (
                        <Badge tone="terracotta">pregunta libre</Badge>
                      )}
                    </div>
                    <div className="text-sm text-carbon-900 whitespace-pre-wrap break-words">
                      {it.question || <span className="text-carbon-400 italic">Sin pregunta libre (se usa el body de la template)</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleActive(it)} data-testid={`dynseq-toggle-${it.position}`}
                      className="text-xs px-2.5 py-1 border border-carbon-200 text-carbon-700 rounded hover:bg-page">
                      {it.active ? "Pausar" : "Activar"}
                    </button>
                    <button onClick={() => setEdit(it)} data-testid={`dynseq-edit-${it.position}`}
                      className="text-xs px-2.5 py-1 border border-carbon-200 text-carbon-700 rounded hover:bg-page flex items-center gap-1.5">
                      <PencilSimple size={12} /> Editar
                    </button>
                    <button onClick={() => remove(it.id)} data-testid={`dynseq-delete-${it.position}`}
                      className="text-xs px-2 py-1 text-signal hover:bg-signal/5 rounded">
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
            {items.length === 0 && (
              <Card className="p-8 text-center text-carbon-500">
                Aún no hay dinámicas en la secuencia. Pulsá “Agregar dinámica” para crear la primera.
              </Card>
            )}
          </div>
        )}
      </PageBody>
      {(showCreate || edit) && (
        <DynamicForm
          initial={edit}
          nextPosition={items.length + 1}
          onClose={() => { setEdit(null); setShowCreate(false); }}
          onSaved={() => { reload(); setEdit(null); setShowCreate(false); }}
        />
      )}
    </>
  );
}

function DynamicForm({ initial, nextPosition, onClose, onSaved }) {
  const [form, setForm] = useState({
    question: initial?.question || "",
    template_id: initial?.template_id || "",
    send_hour: initial?.send_hour || "09:00",
    timezone: initial?.timezone || "America/Guatemala",
    active: initial?.active !== undefined ? initial.active : true,
  });
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/whatsapp/templates", { params: { pipeline: "founder" } })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        if (!cancelled) setTemplates(items.filter((t) => t.status === "approved"));
      })
      .catch(() => { /* */ });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() && !form.template_id) {
      toast.error("Escribí una pregunta libre o elegí una template aprobada.");
      return;
    }
    const payload = {
      question: form.question.trim() || null,
      template_id: form.template_id || null,
      send_hour: form.send_hour,
      timezone: form.timezone,
      active: form.active,
    };
    setSaving(true);
    try {
      if (initial) await api.patch(`/founder/dynamic-sequence/${initial.id}`, payload);
      else await api.post("/founder/dynamic-sequence", payload);
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const selectedTpl = templates.find((t) => t.id === form.template_id);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" data-testid="dynseq-form">
        <div className="font-heading text-lg mb-1">{initial ? `Editar dinámica · posición ${initial.position}` : `Nueva dinámica · posición ${nextPosition}`}</div>
        <p className="text-xs text-carbon-500 mb-4">
          Definí la pregunta que se enviará a los fundadores, o elegí una template aprobada por Meta. La hora de envío se respeta por día.
        </p>
        <div className="space-y-4 text-sm">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Pregunta libre {form.template_id && "(opcional con template — se inyecta como {{2}})"}</span>
            <textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Ej: ¿Qué es lo que más extrañas de tu tierra?"
              data-testid="dynseq-question-input"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" rows={3} />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Template WhatsApp (opcional · solo aprobadas)</span>
            <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })}
              data-testid="dynseq-template-select"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
              <option value="">— Sin template (pregunta libre) —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.language})</option>)}
            </select>
            {templates.length === 0 && (
              <div className="text-[11px] text-warning mt-1">No hay templates aprobadas en el pipeline Founder. Podés usar pregunta libre.</div>
            )}
            {selectedTpl?.body_text && (
              <div className="bg-page border border-carbon-200 rounded p-2 mt-2 text-[11px] text-carbon-700 whitespace-pre-wrap">
                {selectedTpl.body_text}
              </div>
            )}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-carbon-500">Hora de envío</span>
              <input type="time" value={form.send_hour} onChange={(e) => setForm({ ...form, send_hour: e.target.value })}
                data-testid="dynseq-hour-input"
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-carbon-500">Zona horaria</span>
              <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
                <option value="America/Guatemala">America/Guatemala</option>
                <option value="America/Mexico_City">America/Mexico_City</option>
                <option value="America/Chicago">America/Chicago</option>
                <option value="America/New_York">America/New_York</option>
                <option value="America/Los_Angeles">America/Los_Angeles</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="dynseq-active-checkbox" />
            <span className="text-sm text-carbon-700">Dinámica activa (se enviará en la secuencia)</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-carbon-500">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="dynseq-save-btn"
            className="text-sm bg-terracotta text-white px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
